import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { useState } from 'react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import ScriptGrid, { type ScriptLayoutType } from '#app/components/script-grid.js'
import { Combobox } from '#app/components/ui/combobox'
import { usePersistedState } from '#app/hooks/use-persisted-state'
import { prisma } from '#app/utils/db.server.js'
import { getServerList } from '#app/utils/selected-server-options.js'
import { getSkillBySlug } from '#app/utils/skills.server.js'

export async function loader({ params }: LoaderFunctionArgs) {

	var category = params.skillSlug;
	const result = await getSkillBySlug(category || "")

	if (result) category = result.name;
	
	invariantResponse(result, 'Skill not found', { status: 404 })

	const scripts = await prisma.script.findMany({
		where: { display: true, creatorId: { not: null }, 
		OR: [
			{ skills: { in: [result.name] } },
			{ category: { in: [result.name] } },
		]},
		orderBy: { updatedAt: "desc" },
		select: { 
			id: true, 
			slug: true, 
			category: true, 
			title: true, 
			updatedAt: true, 
			creator: { 
				select: { username: true } 
			}, 
			server: true, 
			description: true,
			reactions: {
				select: {
					type: true
				}
			}
		}
	});

	// Process reactions
	const scriptsWithReactions = scripts.map(script => {
		const reactionCounts = script.reactions.reduce((acc, reaction) => {
			acc[reaction.type] = (acc[reaction.type] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);

		const reactions = Object.entries(reactionCounts).map(([type, count]) => ({
			type,
			count
		}));

		return {
			...script,
			reactions
		};
	});

	return json({
		scripts: scriptsWithReactions,
		skill: result
	});

}

export default function SkillCategoryRoute() {
	const { scripts, skill} = useLoaderData<typeof loader>()
    const [selectedServer, setSelectedServer] = usePersistedState<string | null>('selectedServer', null)
    const serverList = getServerList()

    const filteredScripts = selectedServer
        ? scripts.filter(script => script.server === selectedServer)
        : scripts

	return (
		<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
			<div className="flex flex-wrap items-center justify-center">
				<h1 className="text-center text-h2">{filteredScripts.length != 1 ? `${filteredScripts.length} Scripts` : `${filteredScripts.length} Script`} about {skill.name}</h1>
			</div>
			<p className="my-2 text-center text-muted-foreground">
				List of scripts added to the site by users like you. Find the perfect script for your needs and <Link to="/script/new" className="underline hover:no-underline">contribute your own</Link> to help others.
			</p>
			<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">
                <div className="w-full max-w-md mb-8">
                    <Combobox
                        name="server"
                        value={selectedServer}
                        onChange={setSelectedServer}
                        options={serverList}
                        label="Filter by Server"
                        placeholder="Select a server"
                    />
                </div>
				<div className="flex flex-col">
					<div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
						{filteredScripts.length > 0 ? filteredScripts.map(script => <ScriptGrid key={script.slug} {...script} className='' />) : <p className="text-center">No scripts found - <Link to="/script/new" className="underline hover:no-underline">consider adding your own</Link></p>}
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.skill.name ?? params.scriptSlug
	return [
		{ title: `${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Script ${displayName} on ${brandName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<div className="text-center">
						<p className="m-4">Cannot find a skill related to: <br />"{params.skillSlug}"<br /> reach out if you think it should be added.</p>
						<Link to="/scripts/skills" className="text-xl underline hover:no-underline">&lt; Back to all skills</Link>
					</div>					
				),
			}}
		/>
	)
}
